/**
 * Sync Status Card
 * Per-connector status display with action buttons
 */

import {
  Play, Pause, RefreshCw, Settings, CheckCircle,
  XCircle, AlertCircle, Loader2, Database, UploadCloud
} from 'lucide-react';
import { PermissionGate } from '@shared/auth';
import { StandardPermission } from '@shared/auth/permissions';
import { typeLabel } from '../../utils/formatters';

const STATUS_STYLES = {
  active: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: CheckCircle, label: 'Active' },
  inactive: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-500', icon: Pause, label: 'Inactive' },
  error: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: XCircle, label: 'Error' },
  syncing: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: RefreshCw, label: 'Syncing' },
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return 'Never';
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const SyncStatusCard = ({
  connector,
  onSync,
  onActivate,
  onPause,
  onSettings,
  onViewData,
  onUploadAudio,
  syncing = false,
  toggling = false,
  isSelected = false,
}) => {
  const style = STATUS_STYLES[connector.status] || STATUS_STYLES.inactive;
  const StatusIcon = style.icon;
  const isPushOnly = connector.connector_type === 'audio_upload';
  const syncDisabled = syncing || connector.status === 'syncing' || connector.status !== 'active';
  const syncTitle = connector.status === 'active'
    ? 'Sync now'
    : connector.status === 'error'
      ? 'Resolve the error before syncing'
      : 'Activate the connector before syncing';
  const uploadDisabled = connector.status !== 'active';
  const uploadTitle = connector.status === 'active'
    ? 'Upload an audio file'
    : 'Activate the connector before uploading';

  return (
    <div className={`rounded-lg border p-4 ${style.bg} ${style.border} ${isSelected ? 'ring-2 ring-primary-400' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-semibold text-gray-900">{connector.display_name}</h3>
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">
            {typeLabel(connector.connector_type)}
          </span>
        </div>
        <div className="flex items-center space-x-1">
          <StatusIcon size={14} className={style.text} />
          <span className={`text-xs font-medium ${style.text}`}>{style.label}</span>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="text-center">
          <p className="text-xs text-gray-400">{isPushOnly ? 'Last Upload' : 'Last Sync'}</p>
          <p
            className="text-xs font-medium text-gray-700"
            title={formatDateTime(connector.last_sync_at)}
          >
            {formatTimeAgo(connector.last_sync_at)}
          </p>
          {connector.last_sync_at && (
            <p className="text-[10px] text-gray-400 mt-0.5">{formatDateTime(connector.last_sync_at)}</p>
          )}
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-400">Interval</p>
          <p className="text-xs font-medium text-gray-700">
            {isPushOnly ? '—' : `${connector.poll_interval_minutes}m`}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-400">Status</p>
          <p className="text-xs font-medium capitalize text-gray-700">{connector.last_sync_status || '—'}</p>
        </div>
      </div>

      {/* Error message */}
      {connector.error_message && (
        <div className="flex items-start space-x-1.5 mb-3 p-2 bg-red-50 rounded border border-red-100">
          <AlertCircle size={12} className="text-red-500 mt-0.5 shrink-0" />
          <p className="text-xs text-red-600 line-clamp-2">{connector.error_message}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center pt-2 border-t border-gray-200/60">
        <div className="flex items-center space-x-1.5">
          {/* Upload/Sync and Pause/Activate are connector_service writes
              (analytics:connectors). View Data and Settings below are reads and
              stay open to anyone who can reach the page. */}
          <PermissionGate permission={StandardPermission.Analytics.CONNECTORS}>
            {isPushOnly ? (
              <button
                onClick={() => onUploadAudio?.(connector)}
                disabled={uploadDisabled}
                className="flex items-center space-x-1 text-xs text-primary-600 border border-primary-300 rounded px-2.5 py-1.5 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={uploadTitle}
                aria-label={uploadTitle}
              >
                <UploadCloud size={12} />
                <span>Upload Audio</span>
              </button>
            ) : (
              <button
                onClick={() => onSync(connector.id)}
                disabled={syncDisabled}
                className="flex items-center space-x-1 text-xs text-gray-600 border border-gray-300 rounded px-2.5 py-1.5 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={syncTitle}
                aria-label={syncTitle}
              >
                {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                <span>Sync</span>
              </button>
            )}
          </PermissionGate>

          <PermissionGate permission={StandardPermission.Analytics.CONNECTORS}>
            {connector.status === 'active' ? (
              <button
                onClick={() => onPause(connector.id)}
                disabled={toggling}
                className="flex items-center space-x-1 text-xs text-amber-600 border border-amber-300 rounded px-2.5 py-1.5 hover:bg-amber-50 disabled:opacity-50 transition-colors"
                title="Pause"
                aria-label="Pause connector"
              >
                {toggling ? <Loader2 size={12} className="animate-spin" /> : <Pause size={12} />}
                <span>{toggling ? 'Pausing...' : 'Pause'}</span>
              </button>
            ) : (
              <button
                onClick={() => onActivate(connector.id)}
                disabled={toggling}
                className="flex items-center space-x-1 text-xs text-green-600 border border-green-300 rounded px-2.5 py-1.5 hover:bg-green-50 disabled:opacity-50 transition-colors"
                title="Activate"
                aria-label="Activate connector"
              >
                {toggling ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                <span>{toggling ? 'Activating...' : 'Activate'}</span>
              </button>
            )}
          </PermissionGate>

          {(isPushOnly || connector.last_sync_status) && (
            <button
              onClick={() => onViewData(connector.id)}
              className="flex items-center space-x-1 text-xs text-primary-600 border border-primary-300 rounded px-2.5 py-1.5 hover:bg-primary-50 transition-colors"
              title="View interactions"
              aria-label="View interactions"
            >
              <Database size={12} />
              <span>Interactions</span>
            </button>
          )}

          <button
            id={`settings-btn-${connector.id}`}
            onClick={() => onSettings(connector.id)}
            className="flex items-center space-x-1 text-xs text-gray-600 border border-gray-300 rounded px-2.5 py-1.5 hover:bg-white transition-colors"
            aria-label="Connector settings"
          >
            <Settings size={14} />
            <span>Settings</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SyncStatusCard;
